import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RateCardHistory } from "@/components/admin/RateCardHistory";
import { CeilingSummary } from "./ratecard/CeilingSummary";
import { ChangesFromDefault } from "./ratecard/ChangesFromDefault";
import { FieldGroup } from "./ratecard/FieldGroup";
import { MatrixEditor } from "./ratecard/MatrixEditor";
import { RateCardToolbar } from "./ratecard/RateCardToolbar";
import { TableEditor } from "./ratecard/TableEditor";
import { useRateCard } from "./ratecard/useRateCard";

/** Nothing is priced differently until Save is pressed, so say so plainly. */
const DirtyNotice = () => (
  <p
    data-testid="rate-card-dirty-notice"
    className="mt-4 flex items-start gap-2 rounded-lg border px-4 py-3 text-[13px] leading-relaxed"
    style={{
      borderColor: "var(--tt-warn-border)",
      backgroundColor: "var(--tt-warn-bg)",
      color: "var(--tt-warn-text)",
    }}
  >
    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
    You have unsaved changes. Nothing is priced differently until you press Save changes.
  </p>
);

/** Single fees, then factor grids, then the ordered band tables. */
const ServiceEditor = ({ spec, draft, onChange }) => (
  <div className="mt-4 space-y-4">
    {spec.groups.map((group) => (
      <FieldGroup
        key={group.title}
        service={spec.service}
        group={group}
        draft={draft}
        onChange={onChange}
      />
    ))}
    {spec.matrices.map((matrix) => (
      <MatrixEditor
        key={matrix.path}
        service={spec.service}
        matrix={matrix}
        draft={draft}
        onChange={onChange}
      />
    ))}
    {spec.tables.map((table) => (
      <TableEditor
        key={table.path}
        service={spec.service}
        table={table}
        draft={draft}
        onChange={onChange}
      />
    ))}
  </div>
);

/**
 * The rate card screen.
 *
 * The whole editor is rendered from the schema the backend sends, so a new fee
 * appears here without a line of UI being written. `useRateCard` owns the draft
 * and every write; this file only lays out the toolbar, the ceilings, the
 * per-service editor and the change log.
 */
export const RateCardEditor = ({ onAuthError }) => {
  const card = useRateCard(onAuthError);
  const [service, setService] = useState("afs");

  if (card.loading) {
    return (
      <div data-testid="rate-card-loading" className="space-y-4 p-5">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const { payload, draft } = card;
  if (!payload || !draft) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">
        We couldn't load the rate card.
        <Button type="button" variant="outline" onClick={card.load} className="ml-3 bg-white">
          Try again
        </Button>
      </div>
    );
  }

  const spec = payload.schema.find((s) => s.service === service) || payload.schema[0];
  const changes = payload.changes || [];

  return (
    <div data-testid="rate-card-editor" className="p-4 sm:p-5">
      <RateCardToolbar
        payload={payload}
        changeCount={changes.length}
        dirty={card.dirty}
        saving={card.saving}
        resetting={card.resetting}
        onSave={card.save}
        onReset={card.reset}
      />

      <CeilingSummary items={payload.summary} />

      {card.dirty ? <DirtyNotice /> : null}

      <Tabs value={service} onValueChange={setService} className="mt-5 max-w-full overflow-x-auto">
        <TabsList data-testid="rate-card-service-tabs" className="w-max">
          {payload.schema.map((item) => (
            <TabsTrigger
              key={item.service}
              value={item.service}
              data-testid={`rate-card-tab-${item.service}`}
            >
              {item.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <p className="mt-3 text-sm text-muted-foreground">{spec.blurb}</p>

      <ServiceEditor spec={spec} draft={draft} onChange={card.change} />

      {changes.length ? <ChangesFromDefault changes={changes} /> : null}

      <RateCardHistory
        refreshKey={card.historyKey}
        onRestored={card.onRestored}
        onAuthError={onAuthError}
      />
    </div>
  );
};
